import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Plus, Bell, LogOut, Building2, Users, UserPlus, Target, CheckSquare, Zap, Menu, X,
} from "lucide-react";
import QuickLeadDialog from "./QuickLeadDialog";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { initials } from "../lib/format";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import FormDialog from "./FormDialog";
import { COMPANY_FIELDS, CONTACT_FIELDS, LEAD_FIELDS, OPPORTUNITY_FIELDS, ACTIVITY_FIELDS } from "../lib/entities";

const QUICK = [
  { key: "company", label: "Nuova Azienda", icon: Building2, fields: COMPANY_FIELDS, endpoint: "/companies", title: "Nuova Azienda" },
  { key: "contact", label: "Nuovo Contatto", icon: Users, fields: CONTACT_FIELDS, endpoint: "/contacts", title: "Nuovo Contatto" },
  { key: "lead", label: "Nuovo Lead", icon: UserPlus, fields: LEAD_FIELDS, endpoint: "/leads", title: "Nuovo Lead" },
  { key: "opportunity", label: "Nuova Opportunità", icon: Target, fields: OPPORTUNITY_FIELDS, endpoint: "/opportunities", title: "Nuova Opportunità" },
  { key: "activity", label: "Nuova Attività", icon: CheckSquare, fields: ACTIVITY_FIELDS, endpoint: "/activities", title: "Nuova Attività" },
];

export default function Topbar({ onMenu }) {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [showRes, setShowRes] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [notifs, setNotifs] = useState({ count: 0, items: [] });
  const [active, setActive] = useState(null);
  const [quickLead, setQuickLead] = useState(false);
  const boxRef = useRef(null);
  const mobileInputRef = useRef(null);

  useEffect(() => { if (mobileSearch) setTimeout(() => mobileInputRef.current?.focus(), 50); }, [mobileSearch]);

  useEffect(() => {
    const load = () => api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => { setResults(data); setShowRes(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowRes(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setShowRes(false); setQ(""); setMobileSearch(false); nav(path); };

  const ResultList = () => (
    <div data-testid="global-search-results" className="absolute left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-xl max-h-[60vh] sm:max-h-96 overflow-y-auto py-1.5 z-50">
      {resultRows.length === 0 ? (
        <p className="px-4 py-3 text-sm text-slate-400">Nessun risultato per "{q}"</p>
      ) : resultRows.map((r, i) => {
        const Icon = r.icon;
        return (
          <button key={i} onClick={() => go(r.path)} data-testid="search-result-item" className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left min-h-[44px]">
            <Icon className="w-4 h-4 text-teal-600 shrink-0" />
            <span className="text-sm font-medium text-slate-800 truncate">{r.label}</span>
            <span className="text-xs text-slate-400 ml-auto truncate shrink-0 max-w-[40%]">{r.sub}</span>
          </button>
        );
      })}
    </div>
  );

  const resultRows = results ? [
    ...results.companies.map((c) => ({ icon: Building2, label: c.legal_name, sub: c.sector, path: `/aziende/${c.id}` })),
    ...results.contacts.map((c) => ({ icon: Users, label: `${c.first_name} ${c.last_name}`, sub: c.job_title, path: `/contatti/${c.id}` })),
    ...results.leads.map((l) => ({ icon: UserPlus, label: l.title, sub: "Lead", path: `/lead` })),
    ...results.opportunities.map((o) => ({ icon: Target, label: o.name, sub: "Opportunità", path: `/opportunita` })),
  ] : [];

  return (
    <header className="h-16 sticky top-0 z-30 flex items-center gap-2 sm:gap-3 px-3 sm:px-5 border-b border-slate-200 backdrop-blur-md bg-white/85">
      <button data-testid="mobile-menu-button" onClick={onMenu} aria-label="Apri menu" className="lg:hidden w-10 h-10 -ml-1 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-700 shrink-0">
        <Menu className="w-5 h-5" />
      </button>

      <div ref={boxRef} className="relative flex-1 max-w-xl hidden md:block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setShowRes(true)}
          placeholder="Cerca aziende, contatti, lead, opportunità…"
          className="w-full h-10 pl-10 pr-4 rounded-lg bg-slate-100 border border-transparent focus:bg-white focus:border-teal-400 outline-none text-sm transition-colors"
        />
        {showRes && <ResultList />}
      </div>
      <div className="flex-1 md:hidden" />
      <button data-testid="mobile-search-button" onClick={() => setMobileSearch(true)} aria-label="Cerca" className="md:hidden w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
        <Search className="w-[18px] h-[18px]" />
      </button>
      {mobileSearch && (
        <div data-testid="mobile-search-bar" className="md:hidden absolute inset-x-0 top-0 h-16 z-40 bg-white border-b border-slate-200 flex items-center gap-2 px-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input ref={mobileInputRef} data-testid="global-search-input-mobile" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results && setShowRes(true)}
              placeholder="Cerca nel CRM…" className="w-full h-10 pl-10 pr-4 rounded-lg bg-slate-100 border border-transparent focus:bg-white focus:border-teal-400 outline-none text-sm" />
            {showRes && <ResultList />}
          </div>
          <button data-testid="mobile-search-close" onClick={() => { setMobileSearch(false); setShowRes(false); setQ(""); }} aria-label="Chiudi ricerca" className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600 shrink-0"><X className="w-5 h-5" /></button>
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="quick-add-button" aria-label="Nuovo" className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5 h-10 w-10 sm:w-auto px-0 sm:px-4 shrink-0">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nuovo</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem data-testid="quick-add-quick-lead" onClick={() => setQuickLead(true)} className="gap-2.5 cursor-pointer bg-teal-50 text-teal-800 focus:bg-teal-100 font-medium">
            <Zap className="w-4 h-4 text-teal-600" /> Lead rapido
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {QUICK.map((qa) => {
            const Icon = qa.icon;
            return (
              <DropdownMenuItem key={qa.key} data-testid={`quick-add-${qa.key}`} onClick={() => setActive(qa)} className="gap-2.5 cursor-pointer">
                <Icon className="w-4 h-4 text-teal-600" /> {qa.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="notifications-bell" className="relative w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
            <Bell className="w-[18px] h-[18px]" />
            {notifs.count > 0 && <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-mono">{notifs.count}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" collisionPadding={12} className="w-[calc(100vw-1.5rem)] sm:w-80 p-0">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between"><p className="font-heading font-semibold text-sm">Notifiche</p>
            {notifs.items.some((n) => n.persistent) && <button data-testid="notifications-read-all" onClick={async () => { await api.post("/notifications/inbox/read-all"); api.get("/notifications").then(({ data }) => setNotifs(data)); }} className="text-xs text-teal-700 hover:underline">Segna tutte lette</button>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifs.items.length === 0 ? <p className="px-4 py-6 text-sm text-slate-400 text-center">Tutto sotto controllo 👌</p> :
              notifs.items.map((n) => (
                <button key={n.id} data-testid={`notification-${n.id}`} onClick={async () => { if (n.persistent) { await api.patch(`/notifications/inbox/${n.id}/read`); api.get("/notifications").then(({ data }) => setNotifs(data)); } nav(n.link); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-50 flex gap-2.5">
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.type === "warning" ? "bg-amber-500" : n.type === "success" ? "bg-emerald-500" : "bg-blue-500"}`} />
                  <span className="min-w-0"><span className="block text-sm text-slate-700">{n.text}</span>{n.sub && <span className="block text-xs text-slate-400">{n.sub}</span>}</span>
                </button>
              ))}
          </div>
        </PopoverContent>
      </Popover>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-profile-menu" className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold font-heading shrink-0" style={{ background: user?.avatar_color || "#0B192C" }}>
            {initials(user?.name || "FP")}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <p className="font-medium">{user?.name}</p>
            <p className="text-xs text-slate-400 font-normal">{user?.email}</p>
            <p className="text-xs text-teal-600 font-normal mt-0.5">{user?.role_label}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => nav("/impostazioni")} className="cursor-pointer">Impostazioni</DropdownMenuItem>
          <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer text-red-600 gap-2"><LogOut className="w-4 h-4" /> Esci</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <QuickLeadDialog open={quickLead} onOpenChange={setQuickLead} />
      {active && (
        <FormDialog
          open={!!active}
          onOpenChange={(o) => !o && setActive(null)}
          title={active.title}
          fields={active.fields}
          endpoint={active.endpoint}
          onSaved={() => { setActive(null); window.dispatchEvent(new Event("crm-refresh")); }}
        />
      )}
    </header>
  );
}
